import { Table, Row, Cell, HeaderRow, HeaderCell, Button } from '@application-production-system/react';
import { useNavigate } from 'react-router-dom';
import { MonthlyStatsResponse, OrganisationDetails, StatisticsService } from '../../api/doc';
import { useAppSelector, useAppDispatch } from '../../hooks';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { selectOrganisations, selectOrganisationStatus, refreshOrganisations } from '../../store/organisationSlice';
import { getErrorMessage, handleError } from '../../assets/constants';
import { ROUTES } from '../../routes';
import { Page } from '../../components/Page/Page';
import { AkSwitch } from '../../components/Switch-ak/Switch';

const OrganisationsPage = () => {
    const organisations = useAppSelector(selectOrganisations);
    const status = useAppSelector(selectOrganisationStatus);
    const navigate = useNavigate();

    const dispatch = useAppDispatch();

    const [orgs, setOrgs] = useState(organisations);
    const [allOrgs, setAllOrgs] = useState(organisations);
    const [products, setProduct] = useState<any>();
    const [statsLoading, setStatsLoading] = useState(true);
    const [checked, setChecked] = useState(false);

    function getCurrentMonth(month: number) {
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        
        return monthNames[month-1];
    }

    const compare = useCallback((a: any, b:any) => {
        if ( a.name.toLowerCase() < b.name.toLowerCase() ){
          return -1;
        }
        if ( a.name.toLowerCase() > b.name.toLowerCase() ){
          return 1;
        }
        return 0;
    }, []);

    const handleSearch = (event:any) => {
        const search = event.target.value;
        if (search === '') {
            if (checked) {
                setOrgs(allOrgs);
            }
            else {
                setOrgs(allOrgs.filter((org) => org.enabled === true));
            }
        }
        else {
            setOrgs(allOrgs.filter((org) => org?.name?.toLowerCase().includes(search.toLowerCase())));
        }
    }

    useEffect(() => {
        setStatsLoading(true);
        StatisticsService.geProductStats()
        .then(response => {
            const products = response;
            setProduct(products);
            setStatsLoading(false);
        })
        .catch((error) => {
            handleError(error);
            setStatsLoading(false);
        })
    },[])

    // Memoize the expensive stats calculation
    const orgsWithStats: OrganisationDetails[] = useMemo(() => {
        if (!products?.organisations || !organisations.length) {
            return organisations;
        }

        const currentMonth = new Date().getMonth() + 1; // Get current month (1-based index)
        const orgDocs = products.organisations;
        
        // Create a map for O(1) lookup instead of O(n) find
        const orgStatsMap = new Map<string, any>();
        orgDocs.forEach((doc: { organisation: { id: string | undefined; }; stats: any[] }) => {
            if (doc.organisation.id) {
                orgStatsMap.set(doc.organisation.id as string, doc);
            }
        });
            
        const orgs = organisations.map((o) => {
            const docDetails = o.id ? orgStatsMap.get(o.id) : undefined;

            let totalReports = 0;
            let totalPages = 0;

            // Calculate total reports and pages across all months
            docDetails?.stats?.forEach((month: { renderStats: { documentCount: number; pageCount: number; }; }) => {
                totalReports += month.renderStats.documentCount;
                totalPages += month.renderStats.pageCount;
            });

            // Find stats for the current month
            const currentMonthStats = docDetails?.stats?.find((month: { month: number; }) => month.month === currentMonth);

            return {
                ...o,
                reports: currentMonthStats?.renderStats.documentCount ?? 0, // Use current month's document count
                pages: currentMonthStats?.renderStats.pageCount ?? 0,       // Use current month's page count
                totalReports: totalReports || 0,
                totalPages: totalPages || 0,
            };
        });
        
        const sorted = [...orgs].sort(compare);
        return sorted;
    }, [organisations, products, compare]);

    useEffect(() => {
        setAllOrgs(orgsWithStats);
        setOrgs(orgsWithStats.filter((org: OrganisationDetails) => org.enabled === true));
    }, [orgsWithStats])
    
    useEffect(() => {
        dispatch(refreshOrganisations());
    }, [])

    const createOrganisationsTable = () => {
        return status !== 200 ? getErrorMessage(status) : (!organisations.length ? "No organisations to display." : (
            <>
            <input style={{width: '25%', height: '25px', border: '1px solid black', borderRadius: '5px', marginBottom: '10px', paddingLeft: '10px'}} id="search" type="text" placeholder='Search Organisations' onChange={handleSearch} />
            <Table border data={orgs} onSort={setOrgs} divider={false}>
                <>
                <HeaderRow>
                    <HeaderCell sortKey='enabled'><b>Status</b></HeaderCell>
                    <HeaderCell sortKey="name"><b>Name</b></HeaderCell>
                    <HeaderCell sortKey="reports"><b>Reports/Pages</b></HeaderCell>
                    <HeaderCell sortKey="totalReports"><b>Total Reports/Pages</b></HeaderCell>
                    <HeaderCell sortKey="plan"><b>Plan</b></HeaderCell>
                    <HeaderCell sortKey="email"><b>Email</b></HeaderCell>
                </HeaderRow>
                {orgs.map((organisation: OrganisationDetails, idx: number) => {
                    return (
                        <Row
                            key={idx}
                            onClick={() => {
                                navigate(ROUTES.organisationDetails.build({id: organisation.id}))
                            }}
                            divider={true}
                            striped={idx % 2 === 1}
                        >
                            <Cell style={{color: organisation.enabled ? "green" : "red"}}>{ organisation.enabled ? "Active": "Disabled" }</Cell>
                            <Cell>{ organisation.name }</Cell>
                            <Cell>
                                {statsLoading ? 'Loading...' : (organisation.reports === 0 ? 'NA' : organisation.reports + " / " + organisation.pages)}
                            </Cell>
                            <Cell>
                                {statsLoading ? 'Loading...' : (organisation.totalReports === 0 ? 'NA' : organisation.totalReports + " / " + organisation.totalPages)}
                            </Cell>
                            <Cell>{ organisation.plan }</Cell>
                            <Cell>{ organisation.email }</Cell>
                        </Row>
                    );
                })}
                </>
            </Table>
            </>
        ))
    }

    const showOrgLength = () => {
        return orgs.length;
    }

    const showActivePlans = () => {
        let PIM01 = 0;
        orgs.forEach(o => {
            if (o.plan === "PIM01" && o.enabled) {
                PIM01++;
            }
        });
        return "Total Active: PIM01 (" + PIM01 + ")";
    }

    const filterOrgs = (e: any) => {
        const checked = e?.nativeEvent?.target?.checked;
        setChecked(checked);
        if (checked) {
            setOrgs(allOrgs);
            return;
        }
        orgs.sort(compare);
        setOrgs(orgs.filter((org) => org.enabled === true));
        return;
    }

    return (
        <Page title={{page: "Organisations", icon: ["far", "flag"]}}>
            <div style={{overflowX: "hidden"}}>
                <div style={{display: "flex"}}>
                    <h2>Total Orgs ({showOrgLength()}) - {showActivePlans()}</h2>
                    <div style={{marginLeft: "auto", marginTop: "auto", marginBottom: "10px", display: "flex", gap: 10}}>
                        <AkSwitch label='Show Disabled' onChange={filterOrgs}/>
                        <Button
                            disabled={status !== 200}
                            title="New User and Org"
                            icon={['far', 'plus-circle']}
                            iconPosition="left"
                            onClick={() => {
                                navigate(ROUTES.createOrgAndUserPage.build());
                            }}
                        />
                        <Button
                            disabled={status !== 200}
                            title="New Org"
                            icon={['far', 'plus-circle']}
                            iconPosition="left"
                            onClick={() => {
                                navigate(ROUTES.createOrganisation.build());
                            }}
                        />
                    </div>
                </div>
                <div style={{overflowX: "scroll"}}>
                    <div style={{minWidth: "500px"}}>
                        {createOrganisationsTable()}
                    </div>
                </div>
            </div>
        </Page>
    );
}

export default OrganisationsPage;
